/**
 * RecettesPage — the RecetteReactif editor. A recipe declares how much of a reagent an analysis
 * consumes per test and per QC point, optionally scoped to an automate + méthode (a wildcard when
 * left blank — an explicit automate/méthode beats the wildcard at resolution time, server-side).
 *
 * The server exposes only a create endpoint (no list), so this is a creation worksheet with inline
 * guidance. It still drives the full 5-state DoD off the reagent catalog (its picker source).
 */
import { useMemo, useState, type FormEvent } from "react";
import {
  Button,
  Card,
  ErrorState,
  ForbiddenState,
  FormField,
  Input,
  LoadingState,
  OfflineState,
  Select,
  useToast,
} from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { ApiError } from "@/api";
import { INV_CAPS } from "../caps";
import { useCreateRecette, useLabId, useReactifs } from "../api";
import { reactifOptions, parseQty } from "../lib";
import { PageHeader } from "../components/PageHeader";

export function RecettesPage() {
  const { t } = useT();
  const toast = useToast();
  const labId = useLabId();
  const online = useUiStore((s) => s.online);
  const canRead = useCan(INV_CAPS.reactifRead);
  const canManage = useCan(INV_CAPS.recetteManage);

  const catalogQuery = useReactifs(labId, canRead && online);
  const reactifs = useMemo(() => catalogQuery.data ?? [], [catalogQuery.data]);
  const options = useMemo(() => reactifOptions(reactifs), [reactifs]);
  const create = useCreateRecette();

  const [analyse, setAnalyse] = useState("");
  const [reactifId, setReactifId] = useState("");
  const [automate, setAutomate] = useState("");
  const [methode, setMethode] = useState("");
  const [qTest, setQTest] = useState("");
  const [qControle, setQControle] = useState("");
  const [touched, setTouched] = useState(false);

  const analyseError = touched && analyse.trim() === "" ? t("inventory.field.required") : null;
  const reactifError = touched && reactifId === "" ? t("inventory.field.required") : null;
  const qTestParsed = parseQty(qTest);
  const qTestError = touched && (qTestParsed == null || qTestParsed <= 0) ? t("inventory.field.qtyInvalid") : null;
  const qControleError =
    touched && qControle.trim() !== "" && parseQty(qControle) == null ? t("inventory.field.qtyInvalid") : null;

  const valid =
    analyse.trim() !== "" &&
    reactifId !== "" &&
    qTestParsed != null &&
    qTestParsed > 0 &&
    (qControle.trim() === "" || parseQty(qControle) != null);

  const reset = () => {
    setAnalyse("");
    setReactifId("");
    setAutomate("");
    setMethode("");
    setQTest("");
    setQControle("");
    setTouched(false);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || !canManage) return;
    const qControleNum = qControle.trim() === "" ? undefined : parseQty(qControle) ?? undefined;
    create.mutate(
      {
        analyse_code: analyse.trim(),
        reactif_id: reactifId,
        ...(automate.trim() !== "" ? { automate_id: automate.trim() } : {}),
        ...(methode.trim() !== "" ? { methode: methode.trim() } : {}),
        quantite_par_test: qTestParsed ?? 0,
        ...(qControleNum !== undefined ? { quantite_par_controle: qControleNum } : {}),
      },
      {
        onSuccess: () => {
          toast.success(t("inventory.recettes.success"));
          reset();
        },
        onError: (err) => toast.error(err instanceof ApiError ? err.message : t("inventory.field.required")),
      },
    );
  };

  const header = <PageHeader title={t("inventory.recettes.title")} subtitle={t("inventory.recettes.subtitle")} />;

  if (!canRead) return <ForbiddenState requiredCapabilities={[INV_CAPS.reactifRead]} />;
  if (!online) {
    return (
      <Screen>
        {header}
        <OfflineState onRetry={() => void catalogQuery.refetch()} cached={reactifs.length > 0} />
      </Screen>
    );
  }
  if (catalogQuery.isLoading) {
    return (
      <Screen>
        {header}
        <LoadingState variant="table" rows={4} />
      </Screen>
    );
  }
  if (catalogQuery.isError) {
    return (
      <Screen>
        {header}
        <ErrorState
          message={catalogQuery.error.message}
          code={catalogQuery.error.code}
          onRetry={() => void catalogQuery.refetch()}
        />
      </Screen>
    );
  }

  return (
    <Screen>
      {header}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)", gap: "var(--nv-s-5)" }}>
        <Card title={t("inventory.recettes.new")}>
          <form onSubmit={submit} noValidate>
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
              <FormField
                label={t("inventory.recettes.analyse")}
                hint={t("inventory.recettes.analyse.hint")}
                required
                {...(analyseError ? { error: analyseError } : {})}
              >
                {(p) => (
                  <Input
                    {...p}
                    autoFocus
                    mono
                    autoComplete="off"
                    value={analyse}
                    invalid={!!analyseError}
                    placeholder="GLU"
                    onChange={(e) => setAnalyse(e.target.value.toUpperCase())}
                  />
                )}
              </FormField>

              <FormField
                label={t("inventory.reactif.pick")}
                required
                {...(reactifError ? { error: reactifError } : {})}
              >
                {(p) => (
                  <Select
                    {...p}
                    value={reactifId}
                    invalid={!!reactifError}
                    placeholder={t("inventory.reactif.pick.placeholder")}
                    options={options}
                    onChange={(e) => setReactifId(e.target.value)}
                  />
                )}
              </FormField>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--nv-s-4)" }}>
                <FormField label={t("inventory.recettes.automate")} hint={t("inventory.recettes.automate.hint")}>
                  {(p) => (
                    <Input {...p} mono autoComplete="off" value={automate} onChange={(e) => setAutomate(e.target.value)} />
                  )}
                </FormField>
                <FormField label={t("inventory.recettes.methode")} hint={t("inventory.recettes.methode.hint")}>
                  {(p) => (
                    <Input {...p} autoComplete="off" value={methode} onChange={(e) => setMethode(e.target.value)} />
                  )}
                </FormField>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--nv-s-4)" }}>
                <FormField
                  label={t("inventory.recettes.qParTest")}
                  hint={t("inventory.recettes.qParTest.hint")}
                  required
                  {...(qTestError ? { error: qTestError } : {})}
                >
                  {(p) => (
                    <Input
                      {...p}
                      mono
                      inputMode="decimal"
                      value={qTest}
                      invalid={!!qTestError}
                      placeholder="1"
                      onChange={(e) => setQTest(e.target.value)}
                    />
                  )}
                </FormField>
                <FormField
                  label={t("inventory.recettes.qParControle")}
                  hint={t("inventory.recettes.qParControle.hint")}
                  {...(qControleError ? { error: qControleError } : {})}
                >
                  {(p) => (
                    <Input
                      {...p}
                      mono
                      inputMode="decimal"
                      value={qControle}
                      invalid={!!qControleError}
                      placeholder="0"
                      onChange={(e) => setQControle(e.target.value)}
                    />
                  )}
                </FormField>
              </div>

              <div style={{ display: "flex", gap: "var(--nv-s-2)", justifyContent: "flex-end" }}>
                <Button type="button" variant="ghost" onClick={reset} disabled={create.isPending}>
                  {t("inventory.action.cancel")}
                </Button>
                <Button type="submit" loading={create.isPending} disabled={!canManage || !valid}>
                  {t("inventory.recettes.submit")}
                </Button>
              </div>
            </div>
          </form>
        </Card>

        <Card title={t("inventory.recettes.help.title")}>
          <p style={{ margin: 0, color: "var(--nv-slate)", lineHeight: 1.6 }}>
            {t("inventory.recettes.help.body")}
          </p>
        </Card>
      </div>
    </Screen>
  );
}

function Screen({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>{children}</div>;
}
